// Chart primitives for v2.5 workspace. Pure SVG, no dependencies.
import React from "react";

/** Sparkline — trend at a glance. `values` = numbers. */
export const Sparkline = ({ values, width = 120, height = 28, stroke = "hsl(var(--accent))", fillOpacity = 0.15 }) => {
    if (!values || values.length < 2) return <div className="text-[10px] text-[hsl(var(--mute))]">—</div>;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = width / (values.length - 1);
    const pts = values.map((v, i) => `${(i * stepX).toFixed(1)},${(height - ((v - min) / range) * (height - 4) - 2).toFixed(1)}`).join(" ");
    const areaPts = `0,${height} ${pts} ${width},${height}`;
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
            <polygon points={areaPts} fill={stroke} fillOpacity={fillOpacity} />
            <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
};

/** Weekly bar chart — evidence per week with axis labels. */
export const WeeklyBars = ({ entries, height = 80, testId }) => {
    // entries: [ [week, count], ... ] chronological
    if (!entries || entries.length === 0) return <div className="text-xs text-[hsl(var(--mute))] italic">No weekly data.</div>;
    const max = entries.reduce((m, [, c]) => Math.max(m, c), 0) || 1;
    return (
        <div data-testid={testId}>
            <div className="flex items-end gap-1" style={{ height }}>
                {entries.map(([w, c]) => (
                    <div key={w} className="flex-1 group relative flex flex-col items-stretch justify-end" title={`${w}: ${c}`}>
                        <div className="w-full bg-[hsl(var(--accent))]/70 hover:bg-[hsl(var(--accent))] rounded-sm transition"
                             style={{ height: `${Math.max(2, (c / max) * height)}px` }} />
                        <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] num text-[hsl(var(--ink))] opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap">{c}</div>
                    </div>
                ))}
            </div>
            <div className="flex gap-1 mt-1">
                {entries.map(([w], i) => (
                    <div key={w} className="flex-1 text-[9px] num text-[hsl(var(--mute))] text-center">
                        {i === 0 || i === entries.length - 1 || i === Math.floor(entries.length / 2) ? w.slice(-3) : ""}
                    </div>
                ))}
            </div>
        </div>
    );
};

/** Composition bar — stacked segmentation of a total by category. */
export const CompositionBar = ({ segments, testId, showLegend = true }) => {
    // segments: [{label, count, color?}]
    const total = segments.reduce((s, x) => s + x.count, 0);
    if (!total) return null;
    const palette = ["hsl(var(--accent))", "hsl(var(--positive))", "hsl(var(--warning))", "#6366f1", "#0ea5e9", "#f97316", "#84cc16", "#a855f7"];
    return (
        <div data-testid={testId}>
            <div className="flex h-2 rounded-sm overflow-hidden">
                {segments.map((s, i) => {
                    const pct = (s.count / total) * 100;
                    return <div key={s.label} title={`${s.label}: ${s.count} (${pct.toFixed(0)}%)`}
                                style={{ width: `${pct}%`, background: s.color || palette[i % palette.length] }} />;
                })}
            </div>
            {showLegend && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {segments.map((s, i) => (
                        <div key={s.label} className="flex items-center gap-1.5 text-[10px]">
                            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: s.color || palette[i % palette.length] }} />
                            <span className="text-[hsl(var(--ink))]">{s.label}</span>
                            <span className="num text-[hsl(var(--mute))]">{s.count}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * HorizontalBars — a simple ranked list of [label, count] rows with a bar
 * proportional to the max. Used for tier-specific rankings (Regions,
 * Countries, Cities), Top Organizations, chain distributions, etc.
 */
export const HorizontalBars = ({ entries, testId, labelWidth = "9rem", accent = "hsl(var(--accent))", emptyLabel = "No data.", linkFor }) => {
    const rows = (entries || []).filter(([, c]) => c > 0);
    if (rows.length === 0) return <div className="text-xs text-[hsl(var(--mute))] italic">{emptyLabel}</div>;
    const max = rows.reduce((m, [, c]) => Math.max(m, c), 0);
    return (
        <ul className="space-y-1" data-testid={testId}>
            {rows.map(([label, count]) => {
                const bar = (
                    <>
                        <span
                            className="truncate flex-shrink-0 text-[hsl(var(--ink-soft))]"
                            style={{ width: labelWidth }}
                            title={label}
                        >{label}</span>
                        <div className="flex-1 h-2 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                            <div className="h-full rounded-sm" style={{ width: `${(count / max) * 100}%`, background: accent, opacity: 0.75 }} />
                        </div>
                        <span className="num text-[10px] w-10 text-right text-[hsl(var(--mute))]">{count}</span>
                    </>
                );
                const to = linkFor ? linkFor(label) : null;
                if (to) {
                    return (
                        <li key={label} className="flex items-center gap-2 text-xs">
                            <a href={to} className="contents hover:text-[hsl(var(--accent-ink))]">{bar}</a>
                        </li>
                    );
                }
                return <li key={label} className="flex items-center gap-2 text-xs">{bar}</li>;
            })}
        </ul>
    );
};

/** Regional strip — horizontal bar segments per world region. */
export const RegionalStrip = ({ regionCounts, testId }) => {
    const REGIONS = ["North America", "Europe", "Asia", "Middle East", "Latin America", "Africa", "Oceania"];
    const rows = REGIONS.map((r) => ({ label: r, count: regionCounts[r] || 0 })).filter((r) => r.count > 0);
    if (rows.length === 0) return <div className="text-xs text-[hsl(var(--mute))] italic">No regional data.</div>;
    const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
    return (
        <ul className="space-y-1" data-testid={testId}>
            {rows.map((r) => (
                <li key={r.label} className="flex items-center gap-2 text-xs">
                    <span className="w-28 text-[hsl(var(--ink-soft))] flex-shrink-0">{r.label}</span>
                    <div className="flex-1 h-2 rounded-sm bg-[hsl(var(--surface-sunken))] overflow-hidden">
                        <div className="h-full bg-[hsl(var(--accent))]/70" style={{ width: `${(r.count / max) * 100}%` }} />
                    </div>
                    <span className="num text-[10px] w-8 text-right text-[hsl(var(--mute))]">{r.count}</span>
                </li>
            ))}
        </ul>
    );
};

/**
 * LineChart — multi-series line chart (pure SVG, responsive via viewBox).
 * series: [{ label, color, values: number[] }] — all series share xLabels.
 * The primary time-series anchor for "Market momentum over time".
 */
export const LineChart = ({ series = [], xLabels = [], height = 170, testId, showLegend = true }) => {
    const valid = series.filter((s) => (s.values || []).length > 1);
    if (valid.length === 0) return <div className="text-xs text-[hsl(var(--mute))] italic">Not enough data for a trend line.</div>;
    const W = 640, H = height, padL = 34, padR = 8, padT = 8, padB = 20;
    const n = Math.max(...valid.map((s) => s.values.length));
    const max = Math.max(1, ...valid.flatMap((s) => s.values));
    const x = (i) => padL + (i / (n - 1)) * (W - padL - padR);
    const y = (v) => padT + (1 - v / max) * (H - padT - padB);
    const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
    return (
        <div data-testid={testId}>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full block" preserveAspectRatio="none" style={{ height }}>
                {ticks.map((t) => (
                    <g key={t}>
                        <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="hsl(var(--line))" strokeWidth="1" strokeDasharray={t === 0 ? "" : "3 4"} />
                        <text x={padL - 5} y={y(t) + 3} textAnchor="end" fontSize="9" fill="hsl(var(--mute))" className="num">{t}</text>
                    </g>
                ))}
                {valid.map((s) => {
                    const pts = s.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
                    return (
                        <g key={s.label}>
                            <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                            {s.values.map((v, i) => (
                                <circle key={i} cx={x(i)} cy={y(v)} r="2.2" fill={s.color}>
                                    <title>{`${xLabels[i] || i}: ${v}`}</title>
                                </circle>
                            ))}
                        </g>
                    );
                })}
                {xLabels.length > 1 && [0, Math.floor((xLabels.length - 1) / 2), xLabels.length - 1].map((i) => (
                    <text key={i} x={x(i)} y={H - 6} textAnchor={i === 0 ? "start" : i === xLabels.length - 1 ? "end" : "middle"} fontSize="9" fill="hsl(var(--mute))" className="num">{xLabels[i]}</text>
                ))}
            </svg>
            {showLegend && (
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    {valid.map((s) => (
                        <div key={s.label} className="flex items-center gap-1.5 text-[10px]">
                            <span className="inline-block w-3 h-0.5 rounded" style={{ background: s.color }} />
                            <span className="text-[hsl(var(--ink))]">{s.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
